import styled from "styled-components/macro";
import {
  lightAccentFail50,
  lightAccentSuccess50,
  lightBorder1,
  lightNeutral50,
  lightSurface2,
  lightTextPlaceholder,
} from "../../../utils/tier0-colors";
import {
  TOTALS_ROW_HEIGHT,
  VERTICAL_TIMELINE_PADDING_HZ,
  formatThousandsWithComma,
} from "../../../utils/tier0-utils";
import { DataDumpAggregate } from "../../types";
import { TIER0_TARGET_PERCENT } from "./VerticalTimeline";
import { formatHoursMinutes } from "utils/timeUtils";
const TotalsRow = ({
  dataDumpAggregate,
  numSkus,
  paddingWidth,
  colWidths,
  totalDowntime,
  totalDowntimePercent,
  scale,
}: {
  dataDumpAggregate: DataDumpAggregate;
  numSkus: number;
  paddingWidth: number;
  colWidths: number[];
  totalDowntime: number;
  totalDowntimePercent: number;
  scale: number;
}) => {
  const totalOutput = dataDumpAggregate.netOutput;
  const totalTarget = dataDumpAggregate.totalPotentialOutput;
  const isAboveTotalTarget =
    Math.round(totalOutput) >= Math.round(totalTarget * TIER0_TARGET_PERCENT);
  const totalPerformancePct =
    totalTarget === 0 ? 0 : Math.round((totalOutput / totalTarget) * 100);
  return (
    <Styles $scale={scale}>
      <table className="totals-table">
        <tbody>
          <tr className="totals-row">
            <td
              className="title"
              colSpan={2}
              style={{ minWidth: paddingWidth + colWidths[0] + colWidths[1] }}
            >
              <div>Totals</div>
            </td>
            <td className="work-orders" style={{ width: colWidths[2] }}>
              {numSkus} SKU{numSkus === 1 ? "" : "s"} This Shift
            </td>
            <td
              className="target thick-right-tick"
              style={{ width: colWidths[3] }}
            >
              {formatThousandsWithComma(Math.round(totalTarget))}
            </td>
            <td
              className="output no-right-tick"
              style={{ width: colWidths[4] }}
            >
              <div
                className="coloredBackground"
                style={{
                  background: isAboveTotalTarget
                    ? lightAccentSuccess50
                    : lightAccentFail50,
                }}
              >
                {formatThousandsWithComma(Math.round(totalOutput))}
              </div>
            </td>
            <td
              className="hourly-performance"
              style={{ minWidth: colWidths[5] }}
            >
              <div>{`(${totalPerformancePct}%)`}</div>
            </td>
            <td className="total-downtime" style={{ minWidth: colWidths[6] }}>
              <div className="flex-container">
                <div className="duration">
                  {formatHoursMinutes(totalDowntime)}
                </div>
                <div className="percent">
                  <div className="percent-value">{`(${Math.round(
                    totalDowntimePercent
                  )}% of shift)`}</div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </Styles>
  );
};
export default TotalsRow;

const Styles = styled.div<{ $scale: number }>`
  table {
    border-spacing: 0;
  }
  border-bottom-left-radius: ${p => p.$scale * 8}px;
  border-bottom-right-radius: ${p => p.$scale * 8}px;
  height: ${p => p.$scale * TOTALS_ROW_HEIGHT}px;
  .totals-table {
    height: 100%;
    width: 100%;
  }
  td:not(:last-child):not(.no-right-tick) {
    position: relative;
    &:after {
      content: "";
      position: absolute;
      top: calc(50% - ${p => p.$scale * 18}px);
      bottom: calc(50% - ${p => p.$scale * 18}px);
      right: 0;
      width: ${p => p.$scale * 1}px;
      background: ${lightNeutral50};
    }
  }
  td.target.thick-right-tick:after {
    top: calc(50% - ${p => p.$scale * 12}px);
    bottom: calc(50% - ${p => p.$scale * 12}px);
    width: ${p => p.$scale * 2}px;
    background: #000;
    transform: translateX(${p => p.$scale * -8}px);
  }
  background: ${lightSurface2};
  border: ${p => p.$scale * 1}px solid ${lightBorder1};
  padding: 0 ${p => p.$scale * VERTICAL_TIMELINE_PADDING_HZ}px 0 0;
  .title,
  .work-orders,
  .target,
  .output,
  .hourly-performance,
  .total-downtime {
    color: #000;
    font-size: ${p => p.$scale * 32}px;
    line-height: ${p => p.$scale * 32}px;
    font-family: "Open Sans", sans-serif;
    font-weight: bold;
  }
  .title > div {
    transform: translateX(${p => p.$scale * 28}px);
  }
  .hourly-performance {
    color: ${lightTextPlaceholder};
    padding-left: ${p => p.$scale * 16}px;
    & > div {
      margin-left: ${p => p.$scale * 10}px;
    }
  }
  .work-orders {
    padding-left: ${p => p.$scale * 32}px;
  }
  .target,
  .output {
    min-width: ${p => p.$scale * 120}px;
    line-height: ${p => p.$scale * 32}px;
  }
  .target {
    padding-left: ${p => p.$scale * 16}px;
  }
  .output {
    .coloredBackground {
      border-radius: ${p => p.$scale * 4}px;
      padding: ${p => p.$scale * 4}px ${p => p.$scale * 8}px;
    }
  }
  .total-downtime {
    min-width: ${p => p.$scale * 520}px;
    color: ${lightTextPlaceholder};
    padding-left: ${p => p.$scale * 32}px;
    height: 100%;
    .flex-container {
      display: flex;
      align-items: center;
    }
    .duration {
      padding-right: ${p => p.$scale * 32}px;
      color: #000;
    }
  }
  .title {
    min-width: ${p => p.$scale * 60}px;
    max-width: ${p => p.$scale * 60}px;
  }
`;
